import { defineComponent as _defineComponent } from 'vue'
import Modal from "@/components/ui/Modal.vue";
  import { computed, reactive, ref, onMounted } from "vue";
  import { useToast } from "vue-toastification";
  import CreateInitialHoldForm from "@/components/initial-hold/CreateInitialHoldForm.vue";
  import store from "@/store";
  import { trackSendInitialHold } from "@/helpers/initialHold/trackSendInitialHold";
  import ConfirmInitialHoldModal from "./ConfirmInitialHoldModal.vue";
  import FormModalLayout from "./FormModalLayout.vue";
  import { CreateInitialHoldWithPartnerInterface } from "@/types/InitialHoldInterface";
  import { calculateStateFromPostcode } from "@/helpers/initialHold/calculateStateFromPostcode";
  const enum OnDaysChangeActions {
    PLUS = "plus",
    MINUS = "minus",
  }

  
export default /*@__PURE__*/_defineComponent({
  __name: 'CreateHoldFromLandownerSideModal',
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    lot: {
      type: Object,
      required: true,
    },
  },
  emits: ["close", "created"],
  setup(__props, { expose: __expose, emit: __emit }) {
  __expose();

  const onAddChannelBuilderOrPartner = () => {
    showFormErrors.value = false;
    isBuilderOrChannelPartnerAdded.value =
      !isBuilderOrChannelPartnerAdded.value;
  };

  const props = __props;
  const emits = __emit;

  const toast = useToast();
  const expirationDate = ref("2");
  const isBuilderOrChannelPartnerAdded = ref(false);
  const showFormErrors = ref(false);
  const isConfirmModalOpen = ref(false);
  const isSubmitting = ref(false);
  const pendingInitialHoldsOnLotCount = ref(0);
  const onFormChange = (
    data: Partial<CreateInitialHoldWithPartnerInterface>
  ) => {
    Object.assign(form, data);
  };

  const addBuilderOrPartnerButtonText = computed(() => {
    const action = !isBuilderOrChannelPartnerAdded.value ? "Add" : "Remove";
    const prefix = !isBuilderOrChannelPartnerAdded.value ? "to" : "from";

    return `${action} Channel Partner or Builder ${prefix} hold`;
  });

  const form = reactive({
    builderOrPartnerFirstName: "",
    builderOrPartnerLastName: "",
    builderOrPartnerEmail: "",
    builderOrPartnerPhone: "",
    builderOrPartnerCompany: "",
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    postcode: "",
    company: "",
    holderType: "",
    isValid: false,
  });

  const onDaysChange = (e: Event) => {
    const MAX_DAYS = 99;
    const operation = (e.target as HTMLInputElement).name;
    const isPlusAction = operation === OnDaysChangeActions.PLUS;
    const isMinusAction = operation === OnDaysChangeActions.MINUS;

    if (!isPlusAction && !isMinusAction) return;

    let currentValue = parseInt(expirationDate.value) || 0;

    if (isMinusAction && currentValue <= 2) {
      toast.error("Hold duration must be greater than 1 day");
      return;
    }

    if (isPlusAction && currentValue < MAX_DAYS) {
      currentValue++;
    } else if (isMinusAction && currentValue > 1) {
      currentValue--;
    }

    expirationDate.value = currentValue.toString();
  };
  const onBack = () => {
    onClose();
  };

  const submit = () => {
    const hasMoreInitialHolds = pendingInitialHoldsOnLotCount.value >= 1;

    if (!validateForm()) {
      return;
    }

    if (hasMoreInitialHolds) {
      isConfirmModalOpen.value = true;
      return;
    }

    createInitialHoldRequest();
  };
  const onConfirmModal = () => {
    isConfirmModalOpen.value = false;
    createInitialHoldRequest();
  };
  const createInitialHoldRequest = () => {
    const data = createInitialHoldBodyRequest();

    isSubmitting.value = true;
    store
      .dispatch("initialHold/createInitialHoldFromLandownerSide", {
        ...data,
        daysUntilExpire: parseInt(expirationDate.value),
      })
      .then(() => {
        trackSendInitialHold(props.lot.estateId);
        isConfirmModalOpen.value = false;
        onClose();
        emits("created");
        toast.success("Hold created successfully");
      })
      .catch(() => {
        toast.error("Something went wrong, please try again later");
      })
      .finally(() => {
        isSubmitting.value = false;
      });
  };
  const validateForm = () => {
    const daysUntilExpire = parseInt(expirationDate.value);

    if (isSubmitting.value) {
      return;
    }

    if (!form.isValid) {
      showFormErrors.value = true;
      toast.error("Please fill in all required fields");
      return false;
    }

    if (daysUntilExpire <= 1) {
      toast.error("Hold duration must be greater than 1 day");
      return false;
    }

    return true;
  };
  const createInitialHoldBodyRequest = () => {
    const { firstName, lastName, phone, holderType, email, postcode } = form;
    const { _id: landownerId } = store.state.user.data;

    const leadDetails = {
      email: email.toLowerCase(),
      firstName,
      lastName,
      phone,
    };
    const {
      builderOrPartnerCompany,
      builderOrPartnerEmail,
      builderOrPartnerFirstName,
      builderOrPartnerLastName,
      builderOrPartnerPhone,
    } = form;

    const builderOrChannelPartnerDetails = {
      email: builderOrPartnerEmail.toLowerCase(),
      firstName: builderOrPartnerFirstName,
      lastName: builderOrPartnerLastName,
      phone: builderOrPartnerPhone,
      companyDetails: builderOrPartnerCompany,
    };

    const state = calculateStateFromPostcode(postcode);

    return {
      lotId: props.lot._id,
      lotTitle: props.lot.title,
      landownerId,
      estateId: store.state.user.selectedEstateId,
      leadDetails,
      holderType,
      state,
      postcode,
      ...(isBuilderOrChannelPartnerAdded.value && {
        builderOrChannelPartnerDetails: builderOrChannelPartnerDetails,
      }),
      isValid: form?.isValid ?? false,
    };
  };

  const getPendingInitialHoldsCountByLot = () => {
    if (!props?.lot?._id) {
      return;
    }

    store
      .dispatch("initialHold/getPendingInitialHoldsCountByLotId", props.lot._id)
      .then((res) => {
        pendingInitialHoldsOnLotCount.value = res.data;
      });
  };

  onMounted(() => {
    getPendingInitialHoldsCountByLot();
  });
  const onClose = () => {
    isBuilderOrChannelPartnerAdded.value = false;
    showFormErrors.value = false;
    emits("close");
  };

const __returned__ = { onAddChannelBuilderOrPartner, props, emits, toast, expirationDate, isBuilderOrChannelPartnerAdded, showFormErrors, isConfirmModalOpen, isSubmitting, pendingInitialHoldsOnLotCount, onFormChange, addBuilderOrPartnerButtonText, form, OnDaysChangeActions, onDaysChange, onBack, submit, onConfirmModal, createInitialHoldRequest, validateForm, createInitialHoldBodyRequest, getPendingInitialHoldsCountByLot, onClose, Modal, computed, reactive, ref, onMounted, get useToast() { return useToast }, CreateInitialHoldForm, get store() { return store }, get trackSendInitialHold() { return trackSendInitialHold }, ConfirmInitialHoldModal, FormModalLayout, get CreateInitialHoldWithPartnerInterface() { return CreateInitialHoldWithPartnerInterface }, get calculateStateFromPostcode() { return calculateStateFromPostcode } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})